import { useEffect, useState } from 'react';
import { Alert, Button, Modal, Select } from './shared';

export const EMPTY_CREDENTIAL = {
    id: '',
    name: '',
    description: '',
    provider: 'git',
    provider_data: { repo_url: '', token: '', default_branch: '' },
    has_token: false,
    environment: '',
};

const PROVIDER_OPTIONS = [
    { value: 'git', label: 'Git (GitHub repo + token)' },
];

const sanitizeId = (s) => (s || '').toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

export default function CredentialFormModal({ open, onClose, initial, isExisting, onSave }) {
    const [form, setForm] = useState(EMPTY_CREDENTIAL);
    const [tokenInput, setTokenInput] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [editingToken, setEditingToken] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (!open) return;
        const merged = { ...EMPTY_CREDENTIAL, ...(initial || {}) };
        merged.provider_data = { ...EMPTY_CREDENTIAL.provider_data, ...(initial?.provider_data || {}) };
        const hasToken = !!merged.has_token || !!merged.provider_data.has_token;
        merged.has_token = hasToken;
        merged.provider_data.token = '';
        setForm(merged);
        setTokenInput('');
        setShowToken(false);
        setEditingToken(!hasToken);
        setErr('');
    }, [open, initial]);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const setData = (k, v) => setForm((p) => ({ ...p, provider_data: { ...p.provider_data, [k]: v } }));

    const submit = async () => {
        setErr('');
        if (!form.id || !form.name) {
            setErr('ID and Name are required');
            return;
        }
        if (form.provider === 'git') {
            if (!form.provider_data.repo_url) {
                setErr('Repository URL is required');
                return;
            }
            if (!form.has_token && !tokenInput.trim()) {
                setErr('Personal access token is required');
                return;
            }
        }

        setSubmitting(true);
        try {
            const data = { ...form.provider_data };
            const tok = tokenInput.trim();
            if (tok) data.token = tok;
            await onSave({
                id: form.id,
                name: form.name,
                description: form.description || null,
                provider: form.provider,
                provider_data: data,
                environment: form.environment || null,
            });
        } catch (e) {
            setErr(e?.message || String(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => !submitting && onClose()} size="lg" title={isExisting ? `Edit credential "${form.id}"` : 'Add credential'}>
            <Modal.Body>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">ID *</label>
                            <input
                                type="text"
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                                placeholder="my-github-cred"
                                value={form.id}
                                disabled={isExisting}
                                onChange={(e) => set('id', sanitizeId(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                            <input
                                type="text"
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="GitHub PAT for skills repo"
                                value={form.name}
                                onChange={(e) => set('name', e.target.value)}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
                        <input
                            type="text"
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={form.description || ''}
                            onChange={(e) => set('description', e.target.value)}
                        />
                    </div>

                    <Select
                        label="Provider *"
                        value={form.provider}
                        options={PROVIDER_OPTIONS}
                        onChange={(e) => set('provider', e.target.value)}
                    />

                    {form.provider === 'git' && (
                        <>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Repository URL *</label>
                                <input
                                    type="url"
                                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="https://github.com/org/repo"
                                    value={form.provider_data.repo_url}
                                    onChange={(e) => setData('repo_url', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Default branch (optional)</label>
                                <input
                                    type="text"
                                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                                    placeholder="main"
                                    value={form.provider_data.default_branch || ''}
                                    onChange={(e) => setData('default_branch', e.target.value)}
                                />
                            </div>

                            <TokenField
                                hasToken={form.has_token}
                                editingToken={editingToken}
                                tokenInput={tokenInput}
                                showToken={showToken}
                                onTokenChange={setTokenInput}
                                onToggleShow={() => setShowToken((v) => !v)}
                                onEdit={() => setEditingToken(true)}
                                onCancelEdit={() => { setEditingToken(false); setTokenInput(''); }}
                            />
                        </>
                    )}

                    {err && <Alert variant="danger">{err}</Alert>}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={submit} loading={submitting}>{isExisting ? 'Save' : 'Add credential'}</Button>
            </Modal.Footer>
        </Modal>
    );
}

function TokenField({ hasToken, editingToken, tokenInput, showToken, onTokenChange, onToggleShow, onEdit, onCancelEdit }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">Personal Access Token *</label>
                {hasToken && !editingToken && (
                    <button type="button" className="text-xs text-blue-500 hover:underline" onClick={onEdit}>
                        Update token
                    </button>
                )}
            </div>
            {hasToken && !editingToken ? (
                <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
                    <span className="text-xs text-emerald-600 font-medium">Token saved</span>
                    <span className="text-xs text-gray-400">— encrypted at rest</span>
                </div>
            ) : (
                <>
                    <div className="relative">
                        <input
                            type={showToken ? 'text' : 'password'}
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                            placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                            value={tokenInput}
                            onChange={(e) => onTokenChange(e.target.value)}
                            autoComplete="new-password"
                        />
                        <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700" onClick={onToggleShow}>
                            {showToken ? 'hide' : 'show'}
                        </button>
                    </div>
                    {hasToken && editingToken && (
                        <button type="button" className="text-xs text-gray-400 hover:text-gray-600 mt-1" onClick={onCancelEdit}>
                            Cancel — keep existing token
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
